/**
 * Data Health (Screen L) — the V1 honesty layer self-report. Binds GET /api/v1/data-health and renders
 * the per-model manifest (model_name + row_count) in a SkyDataGrid, each row badged via deriveBadge from
 * a Provenance object built from that row's own columns (data_status / provenance_feed / gap_reason /
 * row_count). A SkyLink leads to the Real-vs-Mock matrix. Manifest rows are model-level metadata (not
 * patient rows) but are still never console.logged.
 */
import {
  SkyDataGrid,
  SkyLink,
  type SkyDataGridColumn,
} from "@skypointcloud/skypoint-ui";
import { useNavigate } from "react-router-dom";
import type {
  DataHealthRow,
  Provenance,
  DataStatus,
  ProvenanceFeed,
} from "@shared/contracts";
import { usePage } from "../lib/usePage";
import { dataHealthRowsSchema } from "../lib/schemas/dataHealth";
import { ProvenanceBadge } from "../provenance/ProvenanceBadge";
import { PageError } from "../components/PageError";
import { PageHeader } from "../shell/PageHeader";
import { PageFooter } from "../shell/PageFooter";

/** Build a Provenance shape from a manifest row so the SAME deriveBadge engine renders its tier. */
function rowProvenance(r: DataHealthRow): Provenance {
  return {
    dataStatus: (r.data_status as DataStatus) ?? "mock",
    provenanceFeed: (r.provenance_feed as ProvenanceFeed) ?? "none",
    gapReason: r.gap_reason ?? null,
    rowCount: r.row_count ?? 0,
  };
}

const columns: SkyDataGridColumn<DataHealthRow>[] = [
  { key: "model_name", header: "Model", width: "44%" },
  { key: "row_count", header: "Row count", width: "20%", align: "right" },
  {
    key: "data_status",
    header: "Provenance",
    width: "36%",
    // H5: constrain the chip to max-content so a tint REAL badge renders as a compact labelled chip
    // inside the cell, never stretched into a full-width green BAR.
    // P1-7: showNote renders the DEFERRED/MOCK feed name as a wrapping caption under the short constant
    // chip ("DEFERRED"/"MOCK"), so a long feed token is fully readable here and never clips the chip.
    renderCell: (_v, row) => (
      <span className="inline-flex w-max">
        <ProvenanceBadge meta={rowProvenance(row)} showNote />
      </span>
    ),
  },
];

export function DataHealth() {
  const navigate = useNavigate();
  const { data, loading, error, retry } = usePage<DataHealthRow[]>(
    "/api/v1/data-health",
    dataHealthRowsSchema,
  );

  return (
    <>
      <PageHeader
        crumbs={[
          { key: "home", label: "skyCare" },
          { key: "data-health", label: "Data Health & Matrix" },
        ]}
        title="Data Health & Matrix"
        description="The V1 honesty layer: every backing model self-reports its row count and where the data came from, so an empty surface always reads as awaiting data, never as a clinical zero."
      />
      <section className="mt-6 flex flex-1 flex-col gap-4">
        <SkyLink
          href="/data-health/matrix"
          onClick={(e) => {
            e.preventDefault();
            navigate("/data-health/matrix");
          }}
        >
          Open the Real-vs-Mock matrix
        </SkyLink>
        {error && <PageError title="Could not load data health" message={error} onRetry={retry} />}
        {/* Loading rule: grid-led screens let SkyDataGrid's `loading` prop own the skeleton — no standalone
            SkyLoader stacked above it. Keep the standalone SkyLoader only on non-grid screens. */}
        <SkyDataGrid<DataHealthRow>
          data={data ?? []}
          columns={columns}
          getRowId={(r, i) => r.model_name ?? String(i)}
          loading={loading}
          emptyMessage="Awaiting feed"
        />
      </section>
      <PageFooter />
    </>
  );
}
